import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { LanguageSelector } from '../components/LanguageSelector';
import { ChefHat, ArrowLeft, FileText, Shield, Scale, BookOpen, Mail, Send, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_META = {
  terms: { icon: Scale, color: '#D4A853' },
  terms_of_use: { icon: BookOpen, color: '#2D6A4F' },
  privacy: { icon: Shield, color: '#0EA5E9' },
  legal_notice: { icon: FileText, color: '#E16833' },
  contact: { icon: Mail, color: '#7C3AED' },
};

const NAV_LABELS = {
  en: { terms: 'Terms of Sale', terms_of_use: 'Terms of Use', privacy: 'Privacy', legal_notice: 'Legal Notice', contact: 'Contact' },
  fr: { terms: 'CGV', terms_of_use: 'CGU', privacy: 'Confidentialite', legal_notice: 'Mentions legales', contact: 'Contact' },
  th: { terms: 'เงื่อนไขการขาย', terms_of_use: 'เงื่อนไขการใช้', privacy: 'ความเป็นส่วนตัว', legal_notice: 'ข้อมูลกฎหมาย', contact: 'ติดต่อ' },
  de: { terms: 'AGB', terms_of_use: 'Nutzung', privacy: 'Datenschutz', legal_notice: 'Impressum', contact: 'Kontakt' },
  es: { terms: 'CGV', terms_of_use: 'Uso', privacy: 'Privacidad', legal_notice: 'Aviso Legal', contact: 'Contacto' },
  ru: { terms: 'Условия', terms_of_use: 'Использование', privacy: 'Конфиденциальность', legal_notice: 'Юридическая', contact: 'Контакты' },
  ar: { terms: 'شروط البيع', terms_of_use: 'شروط الاستخدام', privacy: 'الخصوصية', legal_notice: 'قانوني', contact: 'اتصل بنا' },
  zh: { terms: '销售条款', terms_of_use: '使用条款', privacy: '隐私', legal_notice: '法律', contact: '联系' },
};

export default function LegalPage() {
  const { pageType } = useParams();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contactForm, setContactForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  const currentPage = pageType || 'terms';
  const meta = PAGE_META[currentPage] || PAGE_META.terms;
  const Icon = meta.icon;
  const labels = NAV_LABELS[language] || NAV_LABELS.en;

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/legal/${currentPage}?lang=${language}`)
      .then(r => setContent(r.data))
      .catch(() => setContent(null))
      .finally(() => setLoading(false));
  }, [currentPage, language]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API}/api/legal/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm),
      });
      toast.success(content?.form_labels?.success || 'Message sent!');
      setContactForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      toast.error('Error sending message');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0C0C0C] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4A853] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0C0C0C] text-white" data-testid="legal-page">
      {/* Header */}
      <header className="border-b border-[#1A1A1A] sticky top-0 bg-[#0C0C0C]/95 backdrop-blur-sm z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <ChefHat className="w-6 h-6 text-[#D4A853]" />
              <span className="font-black text-sm sm:text-base">HIBI<span className="text-[#D4A853]">SAAS</span></span>
            </Link>
            <Link to="/" className="text-xs text-[#666] hover:text-white flex items-center gap-1 transition-colors">
              <ArrowLeft className="w-3 h-3" />
              <span className="hidden sm:inline">{language === 'fr' ? 'Retour' : 'Back'}</span>
            </Link>
          </div>
          <LanguageSelector variant="dark" />
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-col lg:flex-row gap-8">
        {/* Sidebar nav */}
        <nav className="lg:w-56 flex-shrink-0">
          <div className="lg:sticky lg:top-20 flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0" style={{ scrollbarWidth: 'none' }}>
            {Object.entries(PAGE_META).map(([key, m]) => {
              const NavIcon = m.icon;
              const isActive = key === currentPage;
              return (
                <Link key={key} to={`/legal/${key}`}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                    isActive
                      ? 'bg-white/10 text-white border border-white/10'
                      : 'text-[#888] hover:text-white hover:bg-white/5'
                  }`}
                  data-testid={`legal-nav-${key}`}>
                  <NavIcon className="w-4 h-4 flex-shrink-0" style={{ color: isActive ? m.color : undefined }} />
                  {labels[key]}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Content */}
        <main className="flex-1 min-w-0">
          {content && currentPage !== 'contact' ? (
            <article data-testid={`legal-content-${currentPage}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${meta.color}20` }}>
                  <Icon className="w-5 h-5" style={{ color: meta.color }} />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold">{content.title}</h1>
                  {content.last_updated && (
                    <p className="text-xs text-[#666] mt-0.5">{language === 'fr' ? 'Mis a jour' : 'Updated'}: {content.last_updated}</p>
                  )}
                </div>
              </div>

              <div className="mt-8 space-y-6">
                {content.sections?.map((s, i) => (
                  <section key={i} className="group">
                    <h2 className="text-base font-semibold text-white mb-2 flex items-center gap-2">
                      <span className="w-1 h-5 rounded-full" style={{ background: meta.color }} />
                      {s.heading}
                    </h2>
                    <div className="text-sm text-[#AAA] leading-relaxed whitespace-pre-line pl-4 border-l border-[#1A1A1A]">
                      {s.text}
                    </div>
                  </section>
                ))}
              </div>
            </article>
          ) : content && currentPage === 'contact' ? (
            <div data-testid="legal-content-contact">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${meta.color}20` }}>
                  <Icon className="w-5 h-5" style={{ color: meta.color }} />
                </div>
                <h1 className="text-xl sm:text-2xl font-bold">{content.title}</h1>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Company info */}
                <div className="space-y-4">
                  <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] p-6 space-y-5">
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-[#D4A853]" />
                      <p className="text-xl font-black text-white">{content.company_name}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-[#888]" />
                      <a href={`mailto:${content.email}`} className="text-sm text-[#D4A853] hover:underline">{content.email}</a>
                    </div>
                  </div>
                </div>

                {/* Contact form */}
                <form onSubmit={handleContactSubmit} className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] p-6 space-y-4" data-testid="contact-form">
                  <Input value={contactForm.name} onChange={(e) => setContactForm(f => ({ ...f, name: e.target.value }))}
                    placeholder={content.form_labels?.name} required
                    className="h-11 bg-[#111] border-[#333] text-white rounded-xl placeholder:text-[#555]"
                    data-testid="contact-name" />
                  <Input type="email" value={contactForm.email} onChange={(e) => setContactForm(f => ({ ...f, email: e.target.value }))}
                    placeholder={content.form_labels?.email} required
                    className="h-11 bg-[#111] border-[#333] text-white rounded-xl placeholder:text-[#555]"
                    data-testid="contact-email" />
                  <select value={contactForm.subject} onChange={(e) => setContactForm(f => ({ ...f, subject: e.target.value }))}
                    required className="w-full h-11 px-3 bg-[#111] border border-[#333] text-white rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/40"
                    data-testid="contact-subject">
                    <option value="" className="bg-[#111]">{content.form_labels?.subject}...</option>
                    {content.form_labels?.subjects?.map(s => (
                      <option key={s} value={s} className="bg-[#111]">{s}</option>
                    ))}
                  </select>
                  <textarea value={contactForm.message} onChange={(e) => setContactForm(f => ({ ...f, message: e.target.value }))}
                    placeholder={content.form_labels?.message} required rows={5}
                    className="w-full px-3 py-2.5 bg-[#111] border border-[#333] text-white rounded-xl text-sm resize-none placeholder:text-[#555] focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/40"
                    data-testid="contact-message" />
                  <Button type="submit" disabled={sending}
                    className="w-full h-11 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold rounded-xl"
                    data-testid="contact-submit">
                    {sending ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : (
                      <><Send className="w-4 h-4 mr-2" /> {content.form_labels?.submit}</>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          ) : (
            <p className="text-[#888]">Content not available.</p>
          )}
        </main>
      </div>

      {/* Footer */}
      <footer className="border-t border-[#1A1A1A] py-8 px-6 mt-12">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <ChefHat className="w-4 h-4 text-[#D4A853]" />
            <span className="font-bold text-xs">HIBI<span className="text-[#D4A853]">SAAS</span></span>
          </div>
          <p className="text-xs text-[#666]">INFINITY-GZ LLC — Sharjah Media City (SHAMS), UAE</p>
        </div>
      </footer>
    </div>
  );
}
